import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import { formatBuildingLabel } from "../utils/apartmentDisplay";
import "../styles/modal.css";

const AgreementModal = ({
  isOpen,
  onClose,
  title,
  content,
  agreementId,
  onAgree,
  userInfo,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef(null);
  const [buildingUnitInfo, setBuildingUnitInfo] = useState(null);

  // 쿠키에서 buildingUnitInfo 가져오기
  useEffect(() => {
    const buildingUnitInfoFromCookie = Cookies.get("buildingUnitInfo");
    if (buildingUnitInfoFromCookie) {
      try {
        const parsedBuildingUnitInfo = JSON.parse(buildingUnitInfoFromCookie);
        setBuildingUnitInfo(parsedBuildingUnitInfo);
      } catch (error) {
        console.error("buildingUnitInfo 파싱 오류:", error);
      }
    }
  }, []);

  useEffect(() => {
    // 모달이 열릴 때마다 스크롤 상태 초기화
    if (isOpen) {
      setHasScrolledToBottom(false);

      // 모달이 열릴 때 스크롤을 맨 위로 이동
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }

      // 스크롤 가능한지 확인
      setTimeout(() => {
        const element = contentRef.current;
        if (element) {
          if (element.scrollHeight <= element.clientHeight + 20) {
            // 스크롤이 필요 없는 경우 자동으로 스크롤 완료 처리
            setHasScrolledToBottom(true);
          }
        }
      }, 50);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const element = e.target;
    // 스크롤이 바닥에 도달했는지 확인 (여유 20px 추가)
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  };

  // 내용이 짧아서 스크롤이 필요없는 경우 자동으로 버튼 활성화
  useEffect(() => {
    if (isOpen && contentRef.current) {
      const element = contentRef.current;
      if (element.scrollHeight <= element.clientHeight) {
        setHasScrolledToBottom(true);
      }
    }
  }, [isOpen, content]);

  const handleConfirm = () => {
    if (hasScrolledToBottom) {
      onAgree(agreementId);
    } else {
      // 스크롤을 자동으로 내려줌
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  // 스크롤을 아래로 자동으로 내리는 함수
  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // 애니메이션 최적화
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.25,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  const contentVariants = {
    hidden: {
      y: "100%",
    },
    visible: {
      y: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
        mass: 0.8,
      },
    },
    exit: {
      y: "100%",
      transition: {
        duration: 0.2,
        ease: [0.32, 0, 0.67, 0], // 부드러운 이징 함수 적용
      },
    },
  };

  // 아파트 이름, 동호수 정보 가져오기
  const apartmentName =
    Cookies.get("apartmentCode") === "Richmond"
      ? "더 리치먼드 평창"
      : Cookies.get("apartmentCode") === "Raycounty"
        ? "레이카운티"
        : Cookies.get("apartmentCode") === "Harringtonplace"
          ? "해링턴플레이스GTX운정"
          : Cookies.get("apartmentCode") === "Singilpark"
            ? "신길파크자이"
            : Cookies.get("apartmentCode") === "DMCArtForest"
              ? "DMC아트포레자이"
              : Cookies.get("apartmentCode") === "GodeokUrbanBridge"
                ? "e편한세상 고덕 어반브릿지"
                : Cookies.get("apartmentCode") === "GeomdanUrbanCenter"
                  ? "e편한세상 검단어반센트로"
                  : Cookies.get("apartmentCode") === "GeumgangPenteriumSeaglo"
                    ? "금강펜테리움 더시글로"
                    : Cookies.get("apartmentCode") ===
                        "GeumgangPenteriumSeagloOffice"
                      ? "아라역 금강펜테리움 더 시글로 1차 오피스텔"
                      : Cookies.get("apartmentCode") === "GangbyeonStarhills"
                        ? "덕소강변스타힐스"
                        : Cookies.get("apartmentCode") === "YangjuOkjeongLH"
                          ? "양주옥정LH엘리프"
                          : Cookies.get("apartmentCode") ===
                              "UlsanGwellCityXiComplex1"
                            ? "울산지웰시티자이 1단지"
                            : Cookies.get("apartmentCode") ===
                                "YeongheungForestPrugioParkBien"
                              ? "영흥숲푸르지오파크비엔"
                              : Cookies.get("apartmentCode") ===
                                  "HillstateYonginGojinStationComplex1"
                                ? "힐스테이트용인고진역1단지"
                                : Cookies.get("apartmentCode") ===
                                    "HillstateYonginGojinStationComplex2"
                                  ? "힐스테이트용인고진역2단지"
                                  : Cookies.get("apartmentCode") ===
                                      "HillstateBongdamPrideCity"
                                    ? "힐스테이트봉담프라이드시티"
                                    : Cookies.get("apartmentCode") ===
                                        "PyeongchonIrvineFirstTheSharp"
                                      ? "평촌어바인퍼스트더샵"
                                      : Cookies.get("apartmentCode") ===
                                          "NaepoNewTownMoaMiraedMegacity2nd"
                                        ? "내포신도시모아미래도메가시티2차"
                                        : Cookies.get("apartmentCode") ===
                                            "BucheonIllumiState"
                                          ? "부천일루미스테이트"
                                          : Cookies.get("apartmentCode") ===
                                              "SongdoStationSeohaeGrandbleThePark"
                                            ? "송도역서해그랑블더파크"
                                            : Cookies.get("apartmentCode") ===
                                                "BupyeongHowstoryUrbanGreen"
                                              ? "부평하우스토리어반그린"
                                              : Cookies.get("apartmentCode") ===
                                                  "ForeonStation5"
                                                ? "포레온스테이션5"
                                                : Cookies.get("apartmentCode") ===
                                                    "HyundaiTerraTowerGuriGalmae"
                                                  ? "현대테라타워구리갈매"
                                                  : Cookies.get("apartmentCode") === "테스트"
                                                    ? "테스트"
                                                    : "";
  const apartmentDong = buildingUnitInfo?.building || "000";
  const apartmentHo = buildingUnitInfo?.unit || "000";
  const residentName = buildingUnitInfo?.applicantName || "구분소유자";
  const apartmentCode = Cookies.get("apartmentCode");
  const isNonApartmentComplex =
    apartmentCode === "ForeonStation5" ||
    apartmentCode === "HyundaiTerraTowerGuriGalmae";
  const assigneeOrgLabel =
    apartmentCode === "ForeonStation5" ? "관리단" : "입주자대표회의";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="agreement-modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="agreement-modal-content"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              maxHeight: "100vh", // 전체 화면 높이로 변경
              height: "100%", // 전체 높이 설정
              borderTopLeftRadius: "0", // 상단 모서리 각도 제거
              borderTopRightRadius: "0", // 상단 모서리 각도 제거
              boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
              willChange: "transform", // 성능 최적화
            }}
          >
            <div className="agreement-modal-header">
              <div className="close-button" onClick={onClose}>
                ×
              </div>
              <h1>{title}</h1>
            </div>

            <div
              className="agreement-modal-body"
              ref={contentRef}
              onScroll={handleScroll}
            >
              <div className="agreement-document">
                {title === "하자보수 및 손해배상청구권 양도" && (
                  <>
                    <h2 className="agreement-title">
                      채권(하자보수에 갈음하는 손해배상
                      <br />
                      청구권) 양도양수증서
                    </h2>
                    <p className="agreement-parties">
                      {apartmentName}
                      {isNonApartmentComplex ? " " : " 아파트 "}
                      {formatBuildingLabel(apartmentDong, apartmentCode)}{" "}
                      {apartmentHo}호 {residentName}
                      <br />
                      (이하, "갑" 이라 한다)와
                      <br />
                      {apartmentName} {assigneeOrgLabel} (이하, "을" 이라 한다)는
                      <br />
                      다음과 같이 계약한다.
                    </p>
                  </>
                )}

                <h2 className="agreement-title">{title}</h2>

                <div className="agreement-text">{content}</div>
              </div>
            </div>

            {!hasScrolledToBottom && (
              <div className="scroll-indicator-bottom" onClick={scrollToBottom}>
                ↓ 내용을 끝까지 읽어주세요 ↓
              </div>
            )}

            <div className="agreement-modal-footer no-border">
              <button
                className="agreement-button"
                onClick={handleConfirm}
                disabled={!hasScrolledToBottom}
                style={{
                  backgroundColor: hasScrolledToBottom ? "#7A4FFF" : "#E1DCFF",
                  color: hasScrolledToBottom ? "white" : "white",
                  cursor: hasScrolledToBottom ? "pointer" : "not-allowed",
                }}
              >
                {hasScrolledToBottom ? "동의합니다" : "끝까지 읽어주세요"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AgreementModal;
